export type WebViewPlatform = "ios" | "android" | null;

export interface WebViewInfo {
  isWebView: boolean;
  platform: WebViewPlatform;
}

/**
 * Detects if the app is running inside a webview container
 * Checks for multiple webview indicators across iOS and Android platforms
 * @returns true if running in a webview, false otherwise
 */
export const isWebView = (): boolean => {
  try {
    const ua = navigator.userAgent;
    const isAndroidWebView =
      /wv|WebView|Android.*AppleWebKit(?!.*Chrome)/i.test(ua);
    const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(
      ua
    );
    return isAndroidWebView || isIOSWebView || /WebView/i.test(ua);
  } catch {
    return false;
  }
};

/**
 * Detects webview platform by analyzing user agent patterns
 * Returns "ios" for iOS webviews, "android" for Android webviews, or null if not in webview
 * @returns WebViewPlatform - "ios" | "android" | null
 */
export const getWebViewPlatform = (): WebViewPlatform => {
  try {
    const ua = navigator.userAgent;

    // Android WebView patterns
    const isAndroidWebView =
      /wv|WebView|Android.*AppleWebKit(?!.*Chrome)/i.test(ua);
    if (isAndroidWebView) {
      return "android";
    }

    // iOS patterns - check for WKWebView or UIWebView
    const isIOSWebView = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(
      ua
    );
    if (isIOSWebView) {
      return "ios";
    }

    // Generic WebView pattern (fallback) - try to detect platform
    if (/WebView/i.test(ua)) {
      if (/Android/i.test(ua)) return "android";
      if (/(iPhone|iPod|iPad)/i.test(ua)) return "ios";
      return null;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Returns comprehensive webview information including detection status and platform
 * @returns WebViewInfo object with isWebView boolean and platform
 */
export const getWebViewInfo = (): WebViewInfo => {
  const platform = getWebViewPlatform();
  return {
    isWebView: platform !== null,
    platform,
  };
};
